import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  HypothesesDecisionMeeting as DecisionMeeting,
  HypothesesDecisionMeetingCounts as DecisionMeetingCounts,
  HypothesesDecisionMeetingCreate as DecisionMeetingCreate,
  HypothesesDecisionMeetingItem as DecisionMeetingItem,
  HypothesesDecisionMeetingItemCreate as DecisionMeetingItemCreate,
  HypothesesDecisionMeetingItemUpdate as DecisionMeetingItemUpdate,
  HypothesesDecisionMeetingRef as DecisionMeetingRef,
  HypothesesDecisionMeetingStatus as DecisionMeetingStatus,
  HypothesesDecisionMeetingSummary as DecisionMeetingSummary,
  HypothesesDecisionMeetingUpdate as DecisionMeetingUpdate,
  HypothesesHypothesisMeetingEntry as HypothesisMeetingEntry,
  HypothesesHypothesisResponseCreate as HypothesisResponseCreate,
  HypothesesHypothesisMeetingList as HypothesisMeetingList,
  HypothesesHypothesisScore as HypothesisScore,
  HypothesesHypothesisScoreValues as HypothesisScoreValues,
  HypothesesMeetingDecisionUpdate as MeetingDecisionUpdate,
  HypothesesMeetingEvent as MeetingEvent,
  HypothesesMeetingItemState as MeetingItemState,
  HypothesesMeetingOutcome as MeetingOutcome,
  HypothesesMeetingOutcomeKind as MeetingOutcomeKind,
  HypothesesMeetingQuote as MeetingQuote,
  HypothesesMeetingRegistrationUpdate as MeetingRegistrationUpdate,
  HypothesesMeetingScores as MeetingScores,
  HypothesesMeetingScreen as MeetingScreen,
  HypothesesMeetingScreenOutcome as MeetingScreenOutcome,
  HypothesesMeetingScreenSlide as MeetingScreenSlide,
} from '../generated/api/types.gen.ts';
import {
  hypothesisMeetingLogMeetings,
  hypothesisMeetingsAddItem,
  hypothesisMeetingsCancel,
  hypothesisMeetingsChangeDecision,
  hypothesisMeetingsConfirm,
  hypothesisMeetingsCounts,
  hypothesisMeetingsCreate,
  hypothesisMeetingsEnd,
  hypothesisMeetingsGet,
  hypothesisMeetingsHandOver,
  hypothesisMeetingsHandOverItem,
  hypothesisMeetingsList,
  hypothesisMeetingsRegister,
  hypothesisMeetingsRemoveItem,
  hypothesisMeetingsReorder,
  hypothesisMeetingsScreen,
  hypothesisMeetingsStart,
  hypothesisMeetingsUpdate,
  hypothesisMeetingsUpdateItem,
  hypothesisMeetingsWriteReason,
} from '../generated/api/sdk.gen.ts';
import { ApiFeil } from './feil.ts';
import { useApiKlient } from './klient.ts';

export type {
  DecisionMeeting,
  DecisionMeetingCounts,
  DecisionMeetingCreate,
  DecisionMeetingItem,
  DecisionMeetingItemCreate,
  DecisionMeetingItemUpdate,
  DecisionMeetingRef,
  DecisionMeetingStatus,
  DecisionMeetingSummary,
  DecisionMeetingUpdate,
  HypothesisMeetingEntry,
  HypothesisMeetingList,
  HypothesisScore,
  HypothesisScoreValues,
  MeetingDecisionUpdate,
  MeetingEvent,
  MeetingItemState,
  MeetingOutcome,
  MeetingOutcomeKind,
  MeetingQuote,
  MeetingRegistrationUpdate,
  MeetingScores,
  MeetingScreen,
  MeetingScreenOutcome,
  MeetingScreenSlide,
};

/**
 * H3.2 — møtet, skjermen i rommet, tallene og beslutningsloggen. Nøklene eies her og bare her
 * (`react-ts-integration`, «Query-nøkler har én eier»). De deler prefikset `hypotheses`, så en
 * bekreftelse — som flytter hypoteser mellom statuser — friskere både møtet og rommets lister.
 *
 * Hver skriving svarer med hele møtet (`DecisionMeeting`), så detaljen settes rett fra svaret og
 * lista og tallene hentes på nytt. Skjermen i rommet har sin egen nøkkel og sin egen, lukkede
 * modell (`MeetingScreen`): den deler aldri cache med møtet, så et tall fra møtet kan ikke havne
 * der ved et uhell (P1, DD-47).
 */
export const moeteNoekkel = {
  alle: ['hypotheses', 'moeter'] as const,
  liste: (filter: MoeteFilter) => ['hypotheses', 'moeter', 'liste', filter] as const,
  counts: ['hypotheses', 'moeter', 'counts'] as const,
  detalj: (id: string) => ['hypotheses', 'moeter', 'detalj', id] as const,
  skjerm: (id: string) => ['hypotheses', 'moeter', 'skjerm', id] as const,
  hypotese: (hypoteseId: string) => ['hypotheses', 'moeter', 'hypotese', hypoteseId] as const,
};

export interface MoeteFilter {
  status?: DecisionMeetingStatus;
  /** «Til deg»: møtene kalleren skal bekrefte, og bekreftede hun registrerte med en begrunnelse som mangler. */
  forMe?: boolean;
}

function kast(res: { response?: Response; data?: unknown; error?: unknown }): never {
  throw ApiFeil.fra(res.response?.status ?? 0, res.error ?? res.data);
}

/** «Møter» og møtedelen av «Til deg». Tjeneren gir dem i fast rekkefølge; lista grupperes i klienten etter tilstand. */
export function useMoeteListe(filter: MoeteFilter, enabled = true) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: moeteNoekkel.liste(filter),
    enabled,
    queryFn: async (): Promise<DecisionMeetingSummary[]> => {
      const res = await hypothesisMeetingsList({ client: klient, query: { ...filter, limit: 100 } });
      if (!res.response?.ok || !res.data) kast(res);
      return (res.data as { items: DecisionMeetingSummary[] }).items;
    },
  });
}

/** Tallene ved «Møter» og «Til deg» — per kaller, aldri hvem (Kh8). */
export function useMoeteTellinger(enabled = true) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: moeteNoekkel.counts,
    enabled,
    queryFn: async (): Promise<DecisionMeetingCounts> => {
      const res = await hypothesisMeetingsCounts({ client: klient });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as DecisionMeetingCounts;
    },
  });
}

/** Ett møte: agendaen, kjøringen, bekreftelsen og loggen er samme lesing. Skriver ingen audit-rad (Kh9). */
export function useMoete(id: string | null) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: moeteNoekkel.detalj(id ?? ''),
    enabled: id !== null,
    queryFn: async (): Promise<DecisionMeeting> => {
      const res = await hypothesisMeetingsGet({ client: klient, path: { meetingId: id! } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as DecisionMeeting;
    },
  });
}

/**
 * Skjermen i rommet. Hentes når vinduet åpnes, og på nytt når kjøringen sier at et utfall er
 * registrert (BroadcastChannel) — aldri med polling.
 */
export function useMoeteSkjerm(id: string) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: moeteNoekkel.skjerm(id),
    queryFn: async (): Promise<MeetingScreen> => {
      const res = await hypothesisMeetingsScreen({ client: klient, path: { meetingId: id } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as MeetingScreen;
    },
  });
}

/** Hypotesens møter (W11b), for leser og opp. */
export function useHypoteseMoeter(hypoteseId: string | null, enabled = true) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: moeteNoekkel.hypotese(hypoteseId ?? ''),
    enabled: enabled && hypoteseId !== null,
    queryFn: async (): Promise<HypothesisMeetingEntry[]> => {
      const res = await hypothesisMeetingLogMeetings({ client: klient, path: { id: hypoteseId! } });
      if (!res.response?.ok || !res.data) kast(res);
      return (res.data as HypothesisMeetingList).items;
    },
  });
}

/** Hva en skriving mot møtet er. Alle svarer med hele møtet. */
export type MoeteHandling =
  | { handling: 'endre'; body: DecisionMeetingUpdate }
  | { handling: 'leggTil'; body: DecisionMeetingItemCreate }
  | { handling: 'endreRad'; hypoteseId: string; body: DecisionMeetingItemUpdate }
  | { handling: 'fjernRad'; hypoteseId: string }
  | { handling: 'rekkefolge'; hypoteseIder: string[] }
  | { handling: 'start' }
  | { handling: 'registrer'; hypoteseId: string; body: MeetingRegistrationUpdate }
  | { handling: 'avslutt' }
  | { handling: 'endreBeslutning'; hypoteseId: string; body: MeetingDecisionUpdate }
  | { handling: 'giVidereRad'; hypoteseId: string; deciderId: string }
  | { handling: 'giVidere'; deciderId: string }
  | { handling: 'bekreft' }
  | { handling: 'begrunnelse'; hypoteseId: string; response: HypothesisResponseCreate };

/**
 * Alle skrivingene mot ett møte. Svaret settes som møtets detalj; lista, tallene og — etter en
 * bekreftelse, en begrunnelse eller en «Gi videre» — rommets hypoteser hentes på nytt, fordi de
 * da har byttet status eller fått låsen av.
 */
export function useMoeteSkriving(moeteId: string) {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (h: MoeteHandling): Promise<DecisionMeeting> => {
      const path = { meetingId: moeteId };
      const res = await (() => {
        switch (h.handling) {
          case 'endre':
            return hypothesisMeetingsUpdate({ client: klient, path, body: h.body });
          case 'leggTil':
            return hypothesisMeetingsAddItem({ client: klient, path, body: h.body });
          case 'endreRad':
            return hypothesisMeetingsUpdateItem({ client: klient, path: { ...path, hypothesisId: h.hypoteseId }, body: h.body });
          case 'fjernRad':
            return hypothesisMeetingsRemoveItem({ client: klient, path: { ...path, hypothesisId: h.hypoteseId } });
          case 'rekkefolge':
            return hypothesisMeetingsReorder({ client: klient, path, body: { hypothesisIds: h.hypoteseIder } });
          case 'start':
            return hypothesisMeetingsStart({ client: klient, path });
          case 'registrer':
            return hypothesisMeetingsRegister({ client: klient, path: { ...path, hypothesisId: h.hypoteseId }, body: h.body });
          case 'avslutt':
            return hypothesisMeetingsEnd({ client: klient, path });
          case 'endreBeslutning':
            return hypothesisMeetingsChangeDecision({ client: klient, path: { ...path, hypothesisId: h.hypoteseId }, body: h.body });
          case 'giVidereRad':
            return hypothesisMeetingsHandOverItem({ client: klient, path: { ...path, hypothesisId: h.hypoteseId }, body: { deciderId: h.deciderId } });
          case 'giVidere':
            return hypothesisMeetingsHandOver({ client: klient, path, body: { deciderId: h.deciderId } });
          case 'bekreft':
            return hypothesisMeetingsConfirm({ client: klient, path });
          case 'begrunnelse':
            return hypothesisMeetingsWriteReason({ client: klient, path: { ...path, hypothesisId: h.hypoteseId }, body: { response: h.response } });
        }
      })();
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as DecisionMeeting;
    },
    // Tjeneren kom først (409), eller rollen holder ikke lenger (403): møtet hentes på nytt, så siden
    // viser det som faktisk gjelder — «bekreftet mens du så på» (W10b).
    onError: (e) => {
      if (e instanceof ApiFeil && (e.status === 409 || e.status === 403)) void queryClient.invalidateQueries({ queryKey: moeteNoekkel.detalj(moeteId) });
    },
    onSuccess: (moete, h) => {
      queryClient.setQueryData(moeteNoekkel.detalj(moeteId), moete);
      void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'moeter', 'liste'] });
      void queryClient.invalidateQueries({ queryKey: moeteNoekkel.counts });
      void queryClient.invalidateQueries({ queryKey: moeteNoekkel.skjerm(moeteId) });
      if (['bekreft', 'begrunnelse', 'giVidere', 'giVidereRad', 'start', 'avslutt', 'leggTil', 'fjernRad'].includes(h.handling)) {
        // Overgangene har skjedd (eller låsen er satt eller tatt av): hypotesene og rommets tall.
        void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'liste'] });
        void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'detalj'] });
        void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'nummer'] });
        void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'counts'] });
        void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'moeter', 'hypotese'] });
      }
    },
  });
}

/** «Nytt møte» (W2). Kalleren blir den som registrerer. */
export function useOpprettMoete() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: DecisionMeetingCreate): Promise<DecisionMeeting> => {
      const res = await hypothesisMeetingsCreate({ client: klient, body });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as DecisionMeeting;
    },
    onSuccess: (moete) => {
      queryClient.setQueryData(moeteNoekkel.detalj(moete.id), moete);
      void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'moeter', 'liste'] });
      void queryClient.invalidateQueries({ queryKey: moeteNoekkel.counts });
    },
  });
}

/** «Avlys møtet …» (DD-53: møtet fjernes). Hypotesene står som de var. */
export function useAvlysMoete() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await hypothesisMeetingsCancel({ client: klient, path: { meetingId: id } });
      if (!res.response?.ok) kast(res);
    },
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: moeteNoekkel.detalj(id) });
      void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'moeter', 'liste'] });
      void queryClient.invalidateQueries({ queryKey: moeteNoekkel.counts });
      void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'detalj'] });
    },
  });
}
